import RestrictionArea from '@app/models/RestrictionArea';
import RestrictionAreaDaySpecification from '@app/models/RestrictionAreaDaySpecification';

export const ImportGoogleMyMapsErrors = Object.freeze({
  none: 'none',
  unknown: 'unknown',
  urlNotValid: 'urlNotValid',
  mapShapeNotValid: 'mapShapeNotValid',
  restrictionAreaNotFound: 'restrictionAreaNotFound',
  notAllowed: 'notAllowed',
});

const IMPORT_GOOGLE_MY_MAPS_API_ERROR_CODES_MAPPING = {
  unknown: ImportGoogleMyMapsErrors.unknown,
  urlNotValid: ImportGoogleMyMapsErrors.urlNotValid,
  mapShapeNotValid: ImportGoogleMyMapsErrors.mapShapeNotValid,
  restrictionAreaNotFound: ImportGoogleMyMapsErrors.restrictionAreaNotFound,
  notAllowed: ImportGoogleMyMapsErrors.notAllowed,
};

const IMPORT_GOOGLE_MY_MAPS_ERROR = {
  hasError: true,
  error: 'unknown',
  errorCode: ImportGoogleMyMapsErrors.unknown,
  restrictionArea: null,
};

export default class RestrictionAreasGateway {
  apiRef = null;

  constructor(api) {
    this.apiRef = api;
  }

  async importGoogleMyMapsLink({ dealerId, restrictionAreaId, mapUrl }) {
    if (!dealerId) {
      throw new Error('api.importGoogleMyMapsLink should get a dealerId');
    }

    if (!restrictionAreaId) {
      throw new Error('api.importGoogleMyMapsLink should get a restrictionAreaId');
    }

    if (!mapUrl) {
      throw new Error('api.importGoogleMyMapsLink should get a mapUrl');
    }

    try {
      const result = await this.apiRef.dealerApiFetch(
        `dealers/${dealerId}/restrictionArea/${restrictionAreaId}/update-map-link`,
        'PATCH',
        { mapUrl, restrictionAreaId },
      )(response => response.json());

      const { hasError } = result;

      const isErrorCodeMapped =
        hasError || !!IMPORT_GOOGLE_MY_MAPS_API_ERROR_CODES_MAPPING[result.errorCode];

      if (!isErrorCodeMapped) {
        //  eslint-ignore-next-line no-console
        console.log(
          `api.importGoogleMyMapsLink does not recognize "${result.errorCode}" as a known error`,
        );
      }

      const errorCode = hasError
        ? IMPORT_GOOGLE_MY_MAPS_API_ERROR_CODES_MAPPING[result.errorCode] ||
          ImportGoogleMyMapsErrors.unknown
        : ImportGoogleMyMapsErrors.none;

      return {
        ...result,
        restrictionArea: result.restrictionArea
          ? convertToRestrictionArea.call({ dealerId }, result.restrictionArea)
          : null,
        errorCode,
      };
    } catch (e) {
      return { ...IMPORT_GOOGLE_MY_MAPS_ERROR };
    }
  }

  async getRestrictionAreas(dealerId) {
    if (!dealerId) return [];

    const responseBody = await this.apiRef.dealerApiFetch(`/dealers/${dealerId}/restrictionArea`)();
    const restrictionAreasData = responseBody.restrictionAreas;

    if (!Array.isArray(restrictionAreasData)) {
      throw new Error('RestrictionAreas is not an array');
    }

    return restrictionAreasData.map(convertToRestrictionArea, { dealerId });
  }

  async createRestictionArea(dealerId, { geoJson, displayName, ...data } = {}) {
    if (!dealerId) {
      throw new Error('RestrictionArea should have a dealerId');
    }

    if (!geoJson) {
      throw new Error('RestrictionArea should have a geoJson');
    }

    if (!displayName) {
      throw new Error('RestrictionArea should have a displayName');
    }

    const body = {
      ...data,
      dealerId,
      displayName,
      geoJson: normalizeGeoJson(geoJson),
    };

    await this.apiRef.dealerApiFetch(`/dealers/${dealerId}/restrictionarea`, 'POST', body)();
  }

  async updateMap(dealerId, { id, geoJson } = {}) {
    if (!dealerId) {
      throw new Error('RestrictionArea should have a dealerId');
    }

    if (!id) {
      throw new Error('RestrictionArea should have an id');
    }

    if (!geoJson) {
      throw new Error('RestrictionArea should have a geoJson');
    }

    const editURL = `/dealers/${dealerId}/restrictionarea/${id}/updateMap`;
    const body = {
      dealerId,
      restrictionAreaId: id,
      geoJson: normalizeGeoJson(geoJson),
    };

    await this.apiRef.dealerApiFetch(editURL, 'PATCH', body)();
  }

  async deleteRestictionArea(dealerId, areaId) {
    if (!dealerId) {
      throw new Error('The dealerId should have been provided');
    }

    if (!areaId) {
      throw new Error('The id of the area should have been provided');
    }

    const deleteURL = `/dealers/${dealerId}/restrictionarea/${areaId}/delete`;

    await this.apiRef.dealerApiFetch(deleteURL, 'DELETE')();
  }
}

function normalizeGeoJson(original) {
  const { features } = original;

  const polygons = features.filter(isPolygon);

  return JSON.stringify({ ...original, features: polygons });
}

function isPolygon({ geometry: { type } }) {
  return type === 'Polygon';
}

function convertToRestrictionArea(restrictionAreaData) {
  const {
    id,
    name,
    displayName = name,
    dealerId = this.dealerId,
    geoJson,
    mapUrl,
  } = restrictionAreaData;
  console.log('data', restrictionAreaData);
  if (typeof id !== 'string' || !id) {
    throw new Error('Incorrect RestrictionArea.id');
  }
  if (typeof displayName !== 'string' || !displayName) {
    throw new Error('Incorrect RestrictionArea.displayName');
  }
  if (typeof dealerId !== 'string' || !dealerId) {
    throw new Error('Incorrect RestrictionArea.dealerId');
  }
  if (geoJson === undefined) {
    throw new Error('Incorrect RestrictionArea.geoJson');
  }
  if (mapUrl && typeof mapUrl !== 'string') {
    throw new Error('Incorrect RestrictionArea.mapUrl');
  }
  console.log('parsing geo json 1');
  let parsedGeoJson;
  try {
    parsedGeoJson = JSON.parse(geoJson);
  } catch (e) {
    console.log('parsing geo json');
    throw new Error(`Incorrect RestrictionArea.geoJson ${e.message}`);
  }

  const model = new RestrictionArea({
    id,
    displayName,
    dealerId,
    geoJson: parsedGeoJson,
    mapUrl,
  });
  console.log('model', model);
  return model;
}



// WEBPACK FOOTER //
// ./src/gateways/RestrictionAreasGateway/index.js